import { FAV_POST, LIKE_POST, AI_ARTWORK_POST_SAME } from '~/graphqls/gqls'
import {
  favPost,
  favPostVariables,
  likePost,
  likePostVariables,
  AffectedType,
  SocialViewType,
  ActionType,
  VisibleType,
  FollowStatus,
  PostTypes,
  SquarePostScope,
  SquarePostType,
  aiArtworkPostSame,
  aiArtworkPostSameVariables,
  AiArtworkSameType,
  AiArtworkPostSameInput,
} from '~/graphqls/types'
import { graphQLErrorMessage } from '~/utils'
import { useMutation } from '@apollo/client'
import { useMemo, useState } from 'react'
import Router, { useRouter } from 'next/router'
import { message } from 'antd'
import { useValidateAuth } from './useValidateAuth'
import { useValidateIntergal } from './useValidateIntergal'
import { useStores } from './useStores'
import { useDebounce } from './useDebounce'

type TOptions = {
  sameAgainSuccess?: () => void
  onActionComplete?: (params?: { type?: string; id?: any }) => void
}
/** 一键同款参数类型 */
export type sameAgainProps = AiArtworkPostSameInput & {
  /** 同款中文名称: 错误文案拼接*/
  sameTypeName?: string
}
export const usePostSocial = (post: PostTypes, options?: TOptions) => {
  const [likePostAction] = useMutation<likePost, likePostVariables>(LIKE_POST)
  const [favPostAction] = useMutation<favPost, favPostVariables>(FAV_POST)
  const [createPostAction] = useMutation<
    aiArtworkPostSame,
    aiArtworkPostSameVariables
  >(AI_ARTWORK_POST_SAME)

  const { validateIsLogin } = useValidateAuth()
  const { IsNotEnough, intergalModal } = useValidateIntergal()
  const router = useRouter()

  const [liked, setLiked] = useState(
    post?.itemSocialInfo?.status?.liked || false
  )
  const [faved, setFaved] = useState(
    post?.itemSocialInfo?.status?.collected || false
  )
  const [likeCount, setLikeCount] = useState(
    post?.itemSocialInfo?.likesCount || 0
  )
  const [favCount, setFavCount] = useState(
    post?.itemSocialInfo?.collectsCount || 0
  )

  const { store } = useStores()

  const canSameAgain = useMemo(() => {
    if (store.userinfo?.base?.uid === post?.user?.uid) return true
    const followStatus = post?.user?.social?.followStatus
    if (post?.visibleType === VisibleType.ONLY_FANS) {
      return (
        followStatus === FollowStatus.ME_ONE_WAY ||
        followStatus === FollowStatus.TWO_WAY
      )
    } else if (post?.visibleType === VisibleType.ONLY_FOLLOW_EACH_OTHER) {
      return followStatus === FollowStatus.TWO_WAY
    } else if (post?.visibleType === VisibleType.ONLY_VOTED) {
      // 投票可见
      return !post?.aiArtworkInfo?.hiddenBaseInfo
    }
    return true
  }, [post, store.userinfo])

  const showSameAgain = useMemo(() => {
    if (store.userinfo?.base?.uid === post?.user?.uid) return true
    const followStatus = post?.user?.social?.followStatus
    if (post?.visibleType === VisibleType.ONLY_FANS) {
      return true
    } else if (post?.visibleType === VisibleType.ONLY_FOLLOW_EACH_OTHER) {
      return followStatus === FollowStatus.TWO_WAY
    } else if (post?.visibleType === VisibleType.ONLY_VOTED) {
      // 投票可见
      return true
    }
    return true
  }, [post, store.userinfo])

  const handleLike = () => {
    if (!validateIsLogin()) return
    let affectedType =
      post.postScope === SquarePostScope.INSIDE
        ? AffectedType.AI_ARTWORK
        : AffectedType.EXTERNAL_AI_ARTWORK
    if (post.type === SquarePostType.NOTE) {
      affectedType = AffectedType.NOTE
    } else if (post.type === SquarePostType.AI_COLLECTION) {
      affectedType = AffectedType.AI_COLLECTION
    }
    try {
      likePostAction({
        variables: {
          input: {
            actionType: !liked ? ActionType.CONFIRM : ActionType.CANCEL,
            affectedType,
            id: post.id,
            viewType: SocialViewType.SQUARE,
          },
        },
      })
      setLikeCount(!liked ? likeCount + 1 : likeCount - 1)
      setLiked(!liked)
      options?.onActionComplete?.({
        type: 'liked',
        id: post.id,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleFav = () => {
    if (!validateIsLogin()) return
    let affectedType =
      post.postScope === SquarePostScope.INSIDE
        ? AffectedType.AI_ARTWORK
        : AffectedType.EXTERNAL_AI_ARTWORK
    if (post.type === SquarePostType.NOTE) {
      affectedType = AffectedType.NOTE
    } else if (post.type === SquarePostType.AI_COLLECTION) {
      affectedType = AffectedType.AI_COLLECTION
    }
    try {
      favPostAction({
        variables: {
          input: {
            actionType: !faved ? ActionType.CONFIRM : ActionType.CANCEL,
            affectedType,
            id: post.id,
            viewType: SocialViewType.SQUARE,
          },
        },
      })
      setFavCount(!faved ? favCount + 1 : favCount - 1)
      setFaved(!faved)
      options?.onActionComplete?.({
        type: 'fav',
        id: post.id,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleSearch = () => {
    Router.push({
      pathname: '/',
      query: {
        image: post.coverImage?.imageUrl as string,
      },
    })
  }

  /** 一键同款（包括图生图） */
  const handleSameAgain = useDebounce(
    async (
      param: sameAgainProps = {
        // 默认一键同款
        sameType: AiArtworkSameType.CREATE_LIKEWISE,
        sameTypeName: '一键同款',
      }
    ) => {
      const { sameType, pictureSameModel, imageId, sameTypeName } = param
      if (!validateIsLogin()) return

      try {
        const res = await createPostAction({
          variables: {
            param: {
              postId: post.id,
              sameType,
              pictureSameModel,
              imageId,
            },
          },
        })
        options?.sameAgainSuccess?.()
        router.push(
          `/ai/${res?.data?.squareModule?.aiArtworkId}?visibleType=${post?.visibleType}`
        )
      } catch (err) {
        if (IsNotEnough(err)) {
          intergalModal()
        } else {
          message.destroy()
          message.error(
            graphQLErrorMessage(err) || sameTypeName + '操作失败，请稍后再试~'
          )
        }
      }
    },
    [post]
  )

  return {
    /**点赞状态 */
    liked,
    /**点赞数量 */
    likeCount,
    /**点赞操作 */
    handleLike,
    /**收藏状态 */
    faved,
    /**收藏数量 */
    favCount,
    /**收藏操作 */
    handleFav,
    /**搜相似 */
    handleSearch,
    /**一键同款 */
    handleSameAgain,
    /**展示一键同款按钮 */
    showSameAgain,
    /** 是否可以一键同款按钮 */
    canSameAgain,
  }
}
